import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Download, Mail, MessageSquare, Eye, RefreshCw } from 'lucide-react';

const BadgePreview = ({ 
  open, 
  onOpenChange, 
  eventId, 
  attendee, 
  onSendEmail,
  onSendWhatsApp,
  onDownload 
}) => {
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({ email: false, whatsapp: false });

  const generatePreview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.post(`/events/${eventId}/badges/generate/${attendee.attendee_id}`);
      setBadgeData(response.data.badge_pdf);
    } catch (error) {
      toast.error('Failed to generate badge preview');
    } finally {
      setLoading(false);
    }
  }, [eventId, attendee?.attendee_id]);

  useEffect(() => {
    if (open && attendee?.attendee_id) {
      generatePreview();
    }
  }, [open, attendee?.attendee_id, generatePreview]);

  const handleDownload = () => {
    if (!badgeData) return;
    
    // Convert base64 to blob and download
    const byteCharacters = atob(badgeData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `badge_${attendee.ticket_id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    toast.success('Badge downloaded!');
  };

  const handleSendEmail = async () => {
    setSending(prev => ({ ...prev, email: true }));
    try {
      await api.post(`/events/${eventId}/badges/send-email/${attendee.attendee_id}`);
      toast.success('Badge sent via email!');
      if (onSendEmail) onSendEmail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(prev => ({ ...prev, email: false }));
    }
  };

  const handleSendWhatsApp = async () => {
    setSending(prev => ({ ...prev, whatsapp: true }));
    try {
      await api.post(`/events/${eventId}/badges/send-whatsapp/${attendee.attendee_id}`);
      toast.success('Badge info sent via WhatsApp!');
      if (onSendWhatsApp) onSendWhatsApp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp');
    } finally {
      setSending(prev => ({ ...prev, whatsapp: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" /> Badge Preview
          </DialogTitle>
          <DialogDescription>
            Preview badge for {attendee?.name} before sending
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Attendee Info Summary */}
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-semibold">{attendee?.name}</p>
              <p className="text-sm text-gray-500">
                {attendee?.ticket_id} • {attendee?.category_name}
              </p>
              <p className="text-sm text-gray-500">{attendee?.email}</p>
            </div>
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: attendee?.category_color || '#3B82F6' }}
            />
          </div>

          {/* Badge Preview Area */}
          <Card className="bg-gray-100">
            <CardContent className="p-4 flex justify-center items-center min-h-[300px]">
              {loading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <p className="text-sm text-gray-500">Generating preview...</p>
                </div>
              ) : badgeData ? (
                <div className="bg-white shadow-lg rounded overflow-hidden">
                  {/* PDF Preview using embed */}
                  <embed
                    src={`data:application/pdf;base64,${badgeData}`}
                    type="application/pdf"
                    width="400"
                    height="250"
                    className="border-0"
                  />
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-500 mb-2">Badge preview not available</p>
                  <Button variant="outline" size="sm" onClick={generatePreview}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Generate Preview
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Badge Info */}
          <div className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
            <p className="font-medium text-blue-700 mb-1">Badge includes:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Event name and branding</li>
              <li>Attendee name: <span className="font-medium">{attendee?.name}</span></li>
              <li>Category: <span className="font-medium">{attendee?.category_name}</span></li>
              <li>Unique QR code for check-in</li>
              <li>Ticket ID: <span className="font-medium">{attendee?.ticket_id}</span></li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1" 
              onClick={handleDownload}
              disabled={!badgeData || loading}
              data-testid="preview-download-btn"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            <Button 
              variant="outline" 
              className="flex-1" 
              onClick={handleSendEmail}
              disabled={loading || sending.email}
              data-testid="preview-send-email-btn"
            >
              {sending.email ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Mail className="h-4 w-4 mr-2" /> Send via Email</>
              )}
            </Button>
            <Button 
              className="flex-1" 
              onClick={handleSendWhatsApp}
              disabled={loading || sending.whatsapp}
              data-testid="preview-send-whatsapp-btn"
            >
              {sending.whatsapp ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><MessageSquare className="h-4 w-4 mr-2" /> Send via WhatsApp</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BadgePreview;
